import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle,
  Check,
  CheckCircle2,
  ExternalLink,
  FileText,
  Loader2,
  Package,
  Share2,
  ShieldCheck,
  Truck,
} from 'lucide-react';
import { format } from 'date-fns';

import PageHeader from '../components/PageHeader.jsx';
import SignaturePad from '../components/SignaturePad.jsx';
import { useConfirm } from '../components/ConfirmDialog.jsx';
import {
  authorizeReleaseNote,
  collectReleaseNote,
  getReleaseNote,
  getReleaseNotePdfBlob,
  pickReleaseNote,
} from '../api/releaseNotes';
import { canShareFiles, sharePdfBlob } from '../utils/share';

/**
 * Mobile Release Note page — mirrors frontend/src/pages/ReleaseNote.jsx
 * structurally so reps see the same workflow on both apps. Reached via
 * deeplink from SaleDetail (Open release note action).
 *
 * Four state-specific panels rotate in based on `rn.status`:
 *
 *   created    → PickPanel        warehouse staff confirms each item picked
 *   picked     → AuthorizePanel   manager signs off
 *   authorized → CollectPanel     rep + customer at handover, signature pad
 *   collected  → CompletedPanel   read-only summary + view stamped PDF
 *
 * Deliberately simpler than the desktop version: no react-query (plain
 * promises so the surrounding pattern matches QuoteDetail/SaleDetail);
 * `apiClient`-based PDF blob fetch instead of `window.open(url)` (auth
 * tokens don't carry through plain hrefs); single inline error region
 * shared across all transitions; mobile-tuned typography and spacing.
 *
 * Design intent: every action panel ends with one big primary button so
 * the rep can run through the workflow one-handed. Confirms use the
 * in-app ConfirmDialog (not window.confirm) for consistency with the
 * rest of the PWA.
 */

// ── Date helper (matches the rest of the PWA) ───────────────────────────
function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return format(new Date(iso), 'd MMM yyyy, HH:mm');
  } catch {
    return '—';
  }
}

// ── Progress bar ────────────────────────────────────────────────────────
//
// Same step labels as the desktop progress bar, but laid out for narrow
// mobile viewports (icons + tiny labels). Each step transitions through
// three visual states: upcoming (gray), active (filled accent), done
// (green check).
const STEPS = [
  { key: 'created',    label: 'Pick',      icon: Package },
  { key: 'picked',     label: 'Authorise', icon: ShieldCheck },
  { key: 'authorized', label: 'Collect',   icon: Truck },
  { key: 'collected',  label: 'Done',      icon: CheckCircle2 },
];

function stepIndex(status) {
  const idx = STEPS.findIndex((s) => s.key === status);
  return idx >= 0 ? idx : 0;
}

function ProgressBar({ status }) {
  const current = stepIndex(status);
  return (
    <div className="flex items-center w-full mb-4">
      {STEPS.map((step, i) => {
        const done = i < current;
        const active = i === current;
        const Icon = step.icon;
        return (
          <div key={step.key} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center shrink-0">
              <div
                className={[
                  'w-9 h-9 rounded-pill flex items-center justify-center border-2 transition-colors',
                  done ? 'bg-green-500 border-green-500 text-white' : '',
                  active ? 'bg-blue-600 border-blue-600 text-white ring-4 ring-blue-100' : '',
                  !done && !active ? 'bg-gray-100 border-gray-300 text-gray-400' : '',
                ].join(' ')}
              >
                {done ? <Check size={16} /> : <Icon size={16} />}
              </div>
              <span
                className={[
                  'text-caption mt-1 font-medium',
                  active ? 'text-blue-600' : done ? 'text-green-600' : 'text-gray-400',
                ].join(' ')}
              >
                {step.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 mx-1 ${i < current ? 'bg-green-500' : 'bg-gray-200'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── created → picked ────────────────────────────────────────────────────
//
// Each item starts unchecked; the rep / warehouse staff taps each one as
// they pick it off the shelf. Backend rejects partial picks (must-pick-all
// rule) so the Confirm button is locked until every item is checked. Items
// are grouped by warehouse since multi-warehouse sales are common.
function PickPanel({ rn, onPick, isPicking }) {
  const [checked, setChecked] = useState(() => new Set());
  const allIds = rn.items.map((i) => i.id);
  const allChecked = allIds.length > 0 && allIds.every((id) => checked.has(id));

  function toggle(id) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setChecked(allChecked ? new Set() : new Set(allIds));
  }

  const byWarehouse = {};
  for (const item of rn.items) {
    const wh = item.warehouse_name || 'Unassigned';
    if (!byWarehouse[wh]) byWarehouse[wh] = [];
    byWarehouse[wh].push(item);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-title font-semibold text-gray-900">Pick items</h3>
        <button
          type="button"
          onClick={toggleAll}
          className="text-small text-blue-700 font-medium min-h-touch px-2"
        >
          {allChecked ? 'Uncheck all' : 'Check all'}
        </button>
      </div>
      <p className="text-small text-gray-500 mb-3">
        Tap each line item as you pick it from the warehouse. The system
        won&apos;t advance until every item is confirmed.
      </p>

      {Object.entries(byWarehouse).map(([wh, items]) => (
        <div key={wh} className="mb-3">
          <div className="text-caption font-semibold text-blue-800 mb-1 flex items-center gap-1.5">
            <Package size={12} />
            {wh}
          </div>
          <div className="space-y-2">
            {items.map((item) => {
              const isChecked = checked.has(item.id);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => toggle(item.id)}
                  className={[
                    'w-full text-left flex items-center gap-3 p-3 rounded-card border min-h-touch transition-colors',
                    isChecked
                      ? 'bg-green-50 border-green-300 ring-1 ring-green-200'
                      : 'bg-white border-gray-200',
                  ].join(' ')}
                >
                  <div
                    className={[
                      'w-6 h-6 rounded shrink-0 flex items-center justify-center border-2 transition-colors',
                      isChecked ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300',
                    ].join(' ')}
                  >
                    {isChecked && <Check size={14} />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-body font-medium text-gray-900 truncate">
                      {item.product_name || '—'}
                    </div>
                    {item.batch_order_number && (
                      <div className="text-caption text-gray-500">
                        Batch: {item.batch_order_number}
                      </div>
                    )}
                  </div>
                  <div className="text-small font-semibold text-gray-700 shrink-0">
                    × {item.quantity}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={() => onPick([...checked])}
        disabled={!allChecked || isPicking}
        className={[
          'w-full mt-2 min-h-touch py-3 rounded-card font-semibold text-white text-body transition-colors flex items-center justify-center gap-2',
          allChecked && !isPicking
            ? 'bg-green-600 active:bg-green-700'
            : 'bg-gray-300',
        ].join(' ')}
      >
        {isPicking ? (
          <>
            <Loader2 size={18} className="animate-spin" /> Confirming…
          </>
        ) : (
          <>
            <CheckCircle2 size={18} /> Confirm all picked ({checked.size}/{allIds.length})
          </>
        )}
      </button>
    </div>
  );
}

// ── picked → authorized ─────────────────────────────────────────────────
//
// One-button transition. Shows who picked + when, plus the item summary,
// so the manager can sanity-check before signing off.
function AuthorizePanel({ rn, onAuthorize, isAuthorizing }) {
  return (
    <div>
      <h3 className="text-title font-semibold text-gray-900 mb-1">Authorise release</h3>
      <p className="text-small text-gray-500 mb-3">
        Picked by{' '}
        <span className="font-medium text-gray-700">{rn.picked_by_name ?? '—'}</span>
        {' '}at {fmtDate(rn.picked_at)}. Review and authorise.
      </p>

      <div className="bg-gray-50 rounded-card p-3 mb-3 space-y-1.5">
        {rn.items.map((item) => (
          <div key={item.id} className="flex items-center gap-2 text-small">
            <CheckCircle2 size={14} className="text-green-500 shrink-0" />
            <span className="text-gray-700 truncate">{item.product_name}</span>
            {item.batch_order_number && (
              <span className="text-gray-400 text-caption">({item.batch_order_number})</span>
            )}
            <span className="ml-auto font-medium">× {item.quantity}</span>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={onAuthorize}
        disabled={isAuthorizing}
        className="w-full min-h-touch py-3 rounded-card font-semibold text-white text-body bg-blue-600 active:bg-blue-700 transition-colors disabled:bg-gray-300 flex items-center justify-center gap-2"
      >
        {isAuthorizing ? (
          <>
            <Loader2 size={18} className="animate-spin" /> Authorising…
          </>
        ) : (
          <>
            <ShieldCheck size={18} /> Authorise release
          </>
        )}
      </button>
    </div>
  );
}

// ── authorized → collected ──────────────────────────────────────────────
//
// This is the moment-of-handover panel. Captures the receiver's name and a
// signature, posts to /collect, and on success the backend stamps the
// final PDF. Submit button stays locked until BOTH fields are filled in
// (signature is a base64 data URI; null until the user lifts their finger
// for the first stroke).
function CollectPanel({ rn, onCollect, isCollecting }) {
  const [receiverName, setReceiverName] = useState('');
  const [signature, setSignature] = useState(null);

  const canSubmit =
    receiverName.trim().length > 0 && Boolean(signature) && !isCollecting;

  return (
    <div>
      <h3 className="text-title font-semibold text-gray-900 mb-1">Record collection</h3>
      <p className="text-small text-gray-500 mb-3">
        Authorised by{' '}
        <span className="font-medium text-gray-700">{rn.authorized_by_name ?? '—'}</span>.
        The receiver (customer or driver) should provide their name and sign below.
      </p>

      <div className="space-y-3">
        <div>
          <label className="block text-small font-medium text-gray-700 mb-1">
            Receiver name
          </label>
          <input
            type="text"
            value={receiverName}
            onChange={(e) => setReceiverName(e.target.value)}
            placeholder="Name of person collecting goods"
            className="w-full px-3 min-h-touch border border-gray-300 rounded-card text-body focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-small font-medium text-gray-700 mb-1">
            Signature
          </label>
          <SignaturePad onChange={setSignature} height={180} />
        </div>

        <button
          type="button"
          onClick={() =>
            onCollect({ receiverName: receiverName.trim(), signature })
          }
          disabled={!canSubmit}
          className={[
            'w-full min-h-touch py-3 rounded-card font-semibold text-white text-body transition-colors flex items-center justify-center gap-2',
            canSubmit ? 'bg-indigo-600 active:bg-indigo-700' : 'bg-gray-300',
          ].join(' ')}
        >
          {isCollecting ? (
            <>
              <Loader2 size={18} className="animate-spin" /> Processing…
            </>
          ) : (
            <>
              <Truck size={18} /> Confirm collection
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ── collected (terminal) ────────────────────────────────────────────────
function CompletedPanel({ rn }) {
  return (
    <div>
      <div className="text-center mb-3">
        <div className="w-14 h-14 bg-green-100 rounded-pill flex items-center justify-center mx-auto mb-2">
          <CheckCircle2 size={28} className="text-green-600" />
        </div>
        <h3 className="text-title font-semibold text-green-700">Collection complete</h3>
        <p className="text-small text-gray-500 mt-1">
          Goods collected by {rn.receiver_name} on {fmtDate(rn.collected_at)}
        </p>
      </div>

      <div className="bg-gray-50 rounded-card p-3 space-y-2 text-small">
        <div className="flex justify-between gap-3">
          <span className="text-gray-500 shrink-0">Picked by</span>
          <span className="font-medium text-right">
            {rn.picked_by_name ?? '—'} · {fmtDate(rn.picked_at)}
          </span>
        </div>
        <div className="flex justify-between gap-3">
          <span className="text-gray-500 shrink-0">Authorised by</span>
          <span className="font-medium text-right">
            {rn.authorized_by_name ?? '—'} · {fmtDate(rn.authorized_at)}
          </span>
        </div>
        <div className="flex justify-between gap-3">
          <span className="text-gray-500 shrink-0">Collected by</span>
          <span className="font-medium text-right">
            {rn.receiver_name} · {fmtDate(rn.collected_at)}
          </span>
        </div>
      </div>

      {rn.receiver_signature && (
        <div className="mt-3">
          <p className="text-small text-gray-500 mb-1">Receiver signature</p>
          <div className="border border-gray-200 rounded-card p-2 bg-white inline-block">
            <img
              src={rn.receiver_signature}
              alt="Receiver signature"
              className="max-h-24"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Status pill (matches Sales / Quotes pages' visual vocabulary) ──────
const STATUS_PILL = {
  created:    'bg-gray-100 text-gray-700',
  picked:     'bg-yellow-100 text-yellow-800',
  authorized: 'bg-blue-100 text-blue-800',
  collected:  'bg-green-100 text-green-800',
};

function StatusPill({ status }) {
  const cls = STATUS_PILL[status] ?? 'bg-gray-100 text-gray-700';
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-pill text-caption font-medium capitalize ${cls}`}
    >
      {status?.replace(/_/g, ' ') ?? 'unknown'}
    </span>
  );
}

// ── Main page ───────────────────────────────────────────────────────────
export default function ReleaseNote() {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();

  const [rn, setRn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Single in-flight transition gate — prevents the rep from
  // double-tapping a panel button, and means we only need one error /
  // PDF-loading region across the page.
  const [transition, setTransition] = useState(null); // 'pick' | 'authorize' | 'collect' | null
  const [transitionError, setTransitionError] = useState(null);

  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  // Share state — separate from pdfLoading so a share-in-flight
  // doesn't disable the View button and vice-versa. Render-time
  // feature check decides whether the share affordance even appears
  // (no point showing a button that does nothing on desktop browsers
  // without the Web Share API).
  const [pdfSharing, setPdfSharing] = useState(false);
  const shareSupported = canShareFiles();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getReleaseNote(id)
      .then((data) => { if (!cancelled) setRn(data); })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err.response?.data?.detail || err.message || 'Failed to load release note',
          );
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <main className="min-h-full px-4 pb-6">
        <PageHeader title="Release Note" icon={FileText} />
        <p className="text-small text-gray-500">Loading…</p>
      </main>
    );
  }

  if (error || !rn) {
    return (
      <main className="min-h-full px-4 pb-6">
        <PageHeader title="Release Note" icon={FileText} />
        <p role="alert" className="text-small text-danger">
          <AlertCircle size={14} className="inline mr-1" />
          {error || 'Release note not found.'}
        </p>
      </main>
    );
  }

  /**
   * Generic transition runner — confirms via the in-app dialog, calls the
   * given API function, drops the response straight into local state so
   * the panel for the new status renders. `extra` lets the caller pass a
   * second arg to the API (e.g. picked-item IDs, collect payload).
   *
   * `onSuccess` fires after the response has been merged into local
   * state. Used by the collect flow to navigate the rep back to the
   * parent sale once the RN is in its terminal state.
   */
  async function runTransition(action, apiFn, confirmConfig, extra, onSuccess) {
    if (confirmConfig) {
      const ok = await confirm(confirmConfig);
      if (!ok) return;
    }
    setTransition(action);
    setTransitionError(null);
    try {
      const updated = extra === undefined
        ? await apiFn(rn.id)
        : await apiFn(rn.id, extra);
      setRn(updated);
      if (onSuccess) onSuccess(updated);
    } catch (err) {
      setTransitionError(
        err.response?.data?.detail || err.message || `Failed to ${action} release note`,
      );
    } finally {
      setTransition(null);
    }
  }

  async function openPdf() {
    setPdfLoading(true);
    setPdfError(null);
    try {
      const blob = await getReleaseNotePdfBlob(rn.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
      // 5s is enough for any browser (incl. iOS Safari) to have
      // fetched the blob bytes into the new tab — see the matching
      // comment on SaleDetail.openPdf.
      setTimeout(() => URL.revokeObjectURL(url), 5_000);
    } catch (err) {
      setPdfError(
        err.response?.data?.detail || err.message || 'Failed to load release note PDF',
      );
    } finally {
      setPdfLoading(false);
    }
  }

  /**
   * Share the release-note PDF via the native OS share sheet.
   *
   * Especially valuable on the collected state: the stamped PDF (with
   * receiver signature embedded) is what reps most often want to send
   * to dispatch / customer immediately after handover. For earlier
   * states, it shares the live preview — useful for forwarding to a
   * manager for authorization remotely.
   */
  async function onSharePdf() {
    setPdfSharing(true);
    setPdfError(null);
    try {
      const blob = await getReleaseNotePdfBlob(rn.id);
      const filename = `${rn.release_number ?? `release-${rn.id}`}.pdf`;
      const result = await sharePdfBlob(blob, filename, {
        title: `Release note ${rn.release_number ?? `#${rn.id}`}`,
        text:  rn.customer_name
          ? `Release note for ${rn.customer_name}${rn.invoice_number ? ` (${rn.invoice_number})` : ''}`
          : undefined,
      });
      if (result.outcome === 'error') {
        setPdfError(result.error?.message || 'Failed to share PDF');
      } else if (result.outcome === 'unsupported') {
        setPdfError('Sharing isn’t supported on this device.');
      }
    } catch (err) {
      setPdfError(
        err.response?.data?.detail || err.message || 'Failed to share release note',
      );
    } finally {
      setPdfSharing(false);
    }
  }

  return (
    <main className="min-h-full px-4 pb-6">
      <PageHeader
        title={rn.release_number ?? `Release #${rn.id}`}
        subtitle={rn.customer_name ? `${rn.customer_name} · ${rn.invoice_number ?? ''}` : (rn.invoice_number ?? undefined)}
        icon={FileText}
      />

      {/* Status badge sits just under the header — gives the rep an
          instant read on where in the workflow this RN is even before
          they look at the panel. */}
      <section className="mb-card mb-3 flex items-center justify-between">
        <div>
          <p className="text-caption text-gray-500">Status</p>
          <div className="mt-1"><StatusPill status={rn.status} /></div>
        </div>
        <div className="text-right">
          <p className="text-caption text-gray-500">Created</p>
          <p className="mt-1 text-small font-medium text-gray-900">
            {fmtDate(rn.created_at)}
          </p>
        </div>
      </section>

      {/* Progress bar — shows all four steps, highlights current */}
      <section className="mb-card mb-3">
        <ProgressBar status={rn.status} />
      </section>

      {/* State-specific panel — only one mounts at a time, transitions
          re-render against the new rn.status returned by the API. */}
      <section className="mb-card mb-3">
        {rn.status === 'created' && (
          <PickPanel
            rn={rn}
            isPicking={transition === 'pick'}
            onPick={(pickedIds) => runTransition(
              'pick',
              pickReleaseNote,
              {
                title:        'Confirm picking',
                message:      'Mark all listed items as picked from the warehouse?',
                confirmLabel: 'Confirm',
                confirmVariant: 'success',
              },
              pickedIds,
            )}
          />
        )}
        {rn.status === 'picked' && (
          <AuthorizePanel
            rn={rn}
            isAuthorizing={transition === 'authorize'}
            onAuthorize={() => runTransition(
              'authorize',
              authorizeReleaseNote,
              {
                title:        'Authorise release',
                message:      'Authorise this release? The rep can then record collection on-site.',
                confirmLabel: 'Authorise',
              },
            )}
          />
        )}
        {rn.status === 'authorized' && (
          <CollectPanel
            rn={rn}
            isCollecting={transition === 'collect'}
            onCollect={(payload) => runTransition(
              'collect',
              collectReleaseNote,
              null, // confirm handled implicitly by the form submit
              payload,
              // After successful collection the RN has nothing more for
              // the rep to do here — the natural next action is "Mark
              // as delivered" on the parent sale. Navigate back with a
              // flash so the success registers, and use replace:true so
              // the back button doesn't re-open the now-completed RN
              // (which would be a confusing dead-end view).
              (updated) => {
                navigate(`/sales/${updated.sale_id}`, {
                  replace: true,
                  state: {
                    flash: `Release note ${updated.release_number} collected by ${updated.receiver_name}`,
                  },
                });
              },
            )}
          />
        )}
        {rn.status === 'collected' && <CompletedPanel rn={rn} />}
      </section>

      {transitionError && (
        <p role="alert" className="mb-3 text-small text-danger px-1">
          <AlertCircle size={14} className="inline mr-1" />
          {transitionError}
        </p>
      )}

      {/* PDF preview / final stamped doc — visible at every state. For
          non-collected states the backend regenerates a live preview on
          each request; for collected it serves the stored stamped PDF.
          The share-icon button is the highest-value affordance on this
          page when the RN is collected — it lets the rep send the
          signed PDF straight to dispatch / customer via WhatsApp /
          email. */}
      <section>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={openPdf}
            disabled={pdfLoading}
            className="mb-card-tap flex items-center gap-3 flex-1 text-left disabled:opacity-60"
          >
            <span className="w-10 h-10 rounded-card bg-indigo-50 flex items-center justify-center shrink-0">
              {pdfLoading ? (
                <Loader2 className="h-5 w-5 text-indigo-700 animate-spin" />
              ) : (
                <FileText className="h-5 w-5 text-indigo-700" />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-body font-medium text-gray-900">
                {rn.status === 'collected' ? 'View release note' : 'Preview release note'}
              </p>
              <p className="text-caption text-gray-500">
                {pdfLoading
                  ? 'Generating PDF…'
                  : rn.status === 'collected'
                    ? 'Stamped PDF · opens in browser'
                    : 'Live preview · opens in browser'}
              </p>
            </div>
            {!shareSupported && (
              <ExternalLink className="h-4 w-4 text-gray-400 shrink-0" />
            )}
          </button>
          {shareSupported && (
            <button
              type="button"
              onClick={onSharePdf}
              disabled={pdfSharing}
              aria-label="Share release note"
              className="mb-card-tap min-w-touch flex items-center justify-center disabled:opacity-60"
            >
              {pdfSharing ? (
                <Loader2 className="h-5 w-5 text-indigo-700 animate-spin" />
              ) : (
                <Share2 className="h-5 w-5 text-indigo-700" />
              )}
            </button>
          )}
        </div>

        {pdfError && (
          <p role="alert" className="mt-2 text-small text-danger px-1">
            {pdfError}
          </p>
        )}

        {rn.notes && (
          <div className="mt-3 mb-card bg-amber-50 border border-amber-200">
            <p className="text-small text-amber-900">
              <span className="font-medium">Notes:</span> {rn.notes}
            </p>
          </div>
        )}

        <p className="mt-3 text-caption text-gray-400 text-center">
          Created by {rn.created_by_name || '—'} · {fmtDate(rn.created_at)}
        </p>
      </section>
    </main>
  );
}
